// FRONT-1698: the thinking slider — six stops (Fast, Medium, High, Very high,
// Pro, Auto) on a heat-coloured track, with a label under every stop. One
// component for the chat box's thinking panel, the phone's thinking panel
// (FRONT-3338: both open from the "Thinking: <level>" pill) and Settings →
// Models, so all three look and behave alike.
// It is a plain, hand-built slider rather than the Radix one because the phone
// composer must keep the keyboard focused in its text box while the slider is
// used (`keepFocus`), and Radix moves focus to its thumb on every press.

import { useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';
import type { ThinkingLevel } from '~/lib/types/llm';
import {
  AUTO_RAMP,
  AUTO_STOP,
  HIGH_STOP,
  along,
  cappedAtHighHint,
  thinkingPillTint,
  THINKING_STOPS,
  TOP_TIER_STOPS,
  levelToSave,
  stopAtPointer,
  stopForKey,
  stopIndexOf,
  trackFill,
} from '~/lib/thinking-stops';

// The empty bar and its stop marks. Both derive from the secondary ink so they
// follow the theme: the bar is a faint tint of it, a mark a clearer one. A mark
// on the coloured fill is a soft wash of the page colour, never a hard hole.
const TRACK = 'color-mix(in srgb, var(--muted-foreground) 16%, transparent)';
const DOT_ON_TRACK = 'color-mix(in srgb, var(--muted-foreground) 60%, transparent)';
const DOT_ON_FILL = 'color-mix(in srgb, var(--background) 55%, transparent)';

export interface ThinkingSliderProps {
  /** The stored level; null/undefined is Auto. */
  value: ThinkingLevel | null | undefined;
  /** Called once per committed move (a click, the end of a drag, a key press). */
  onChange: (level: ThinkingLevel | null) => void;
  /** Why the slider cannot be used right now; null when it can. Disabled iff set. */
  disabledReason?: string | null;
  ariaLabel?: string;
  /** Never take focus on press — the phone composer's text box must keep it. */
  keepFocus?: boolean;
  /**
   * The model in use has no Very high / Pro of its own and thinks at High
   * there. Those two stops stay choosable (the level is the person's, and
   * applies on models that have them) but are drawn muted, and the hint says so.
   */
  cappedAtHigh?: boolean;
  /**
   * Hover hint on the slider. A panel that already prints the chosen level's line
   * turns it off: the hint would repeat that line, and a tooltip opened by the
   * panel's initial focus swallows the first Escape (Radix dismissable layers).
   */
  tooltip?: boolean;
  /** The surface's text colour; the chosen stop's name is mixed toward it to stay readable. */
  ink?: string;
  testId?: string;
  className?: string;
}

export function ThinkingSlider({
  value,
  onChange,
  disabledReason = null,
  ariaLabel = 'Thinking',
  keepFocus = false,
  cappedAtHigh = false,
  tooltip = true,
  ink,
  testId = 'thinking-slider',
  className,
}: ThinkingSliderProps) {
  // The drawn bar, not the padded hit area around it: marks are placed on it.
  const barRef = useRef<HTMLDivElement>(null);
  const reasonId = useId();
  const [dragStop, setDragStop] = useState<number | null>(null);
  const disabled = disabledReason !== null;
  const committed = stopIndexOf(value);
  const index = dragStop ?? committed;
  const stop = THINKING_STOPS[index]!;

  const commit = (next: number) => {
    const level = levelToSave(next, value);
    if (level !== undefined) onChange(level);
  };
  const stopFromPointer = (e: PointerEvent) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return committed;
    return stopAtPointer(e.clientX, rect.left, rect.width);
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    // Cancelling pointerdown also cancels the mousedown that would move focus.
    if (keepFocus) e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragStop(stopFromPointer(e));
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStop === null) return;
    setDragStop(stopFromPointer(e));
  };
  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    if (dragStop === null) return;
    const next = stopFromPointer(e);
    setDragStop(null);
    // Became unavailable mid-drag (an answer started): drop the move.
    if (!disabled) commit(next);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const next = stopForKey(e.key, committed);
    if (next === null) return;
    e.preventDefault();
    commit(next);
  };

  const capped = cappedAtHigh && TOP_TIER_STOPS.has(index);
  // The chosen stop's thumb and name: High's colour when capped (it thinks at High there, as the
  // pill says). The name is mixed toward the text colour so a cyan or orange one stays readable at 11px.
  const chosenColor = capped ? THINKING_STOPS[HIGH_STOP]!.color : stop.color;
  const chosenNameColor = thinkingPillTint(chosenColor, ink).nameColor;
  const hint =
    disabledReason ??
    (capped
      ? `Thinking: ${stop.label}. ${cappedAtHighHint(stop.label)}`
      : `Thinking: ${stop.label}. ${stop.hint}`);

  const slider = (
    <div
      role="slider"
      tabIndex={disabled ? -1 : 0}
      aria-label={ariaLabel}
      aria-valuemin={0}
      aria-valuemax={AUTO_STOP}
      aria-valuenow={index}
      aria-valuetext={stop.label}
      aria-disabled={disabled || undefined}
      aria-describedby={disabled ? reasonId : undefined}
      data-testid={testId}
      data-level={stop.level ?? 'auto'}
      data-capped={capped || undefined}
      onKeyDown={onKeyDown}
      className={cn(
        'group flex w-full max-w-md select-none flex-col items-stretch gap-1.5 rounded-full outline-none',
        'focus-visible:ring-2 focus-visible:ring-ring/50',
        disabled && 'opacity-50',
        className,
      )}
    >
      <div
        data-testid={`${testId}-track`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDragStop(null)}
        className={cn(
          'relative flex h-8 w-full touch-none items-center px-2',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <div ref={barRef} className="relative h-1.5 w-full rounded-full" style={{ background: TRACK }}>
          <div
            data-testid={`${testId}-fill`}
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-150 motion-reduce:transition-none"
            style={{ width: along(index / AUTO_STOP), background: trackFill(index) }}
          />
          {/* Small and soft: a stop is a mark on the bar, not a second control —
              the chosen one is the thumb, several times its size. Drawn as SVG
              circles so a mark at a fractional pixel stays round (a 4px CSS
              box snaps to 4 or 5px and reads as an oval). The box is inset like
              `along`, so a circle's cx is the same fraction. */}
          <svg aria-hidden="true" className="absolute inset-y-0 left-[3px] h-full w-[calc(100%-6px)] overflow-visible">
            {THINKING_STOPS.map((s, i) => (
              <circle
                key={s.label}
                data-testid={`${testId}-dot`}
                cx={`${(i / AUTO_STOP) * 100}%`}
                cy="50%"
                r={2}
                fill={i <= index ? DOT_ON_FILL : DOT_ON_TRACK}
                opacity={i === index ? 0 : cappedAtHigh && TOP_TIER_STOPS.has(i) ? 0.45 : 1}
              />
            ))}
          </svg>
          <span
            aria-hidden="true"
            className={cn(
              'absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background',
              'transition-[left] duration-150 motion-reduce:transition-none',
              !disabled && 'group-hover:scale-110',
            )}
            style={{
              left: along(index / AUTO_STOP),
              background: index === AUTO_STOP ? AUTO_RAMP : chosenColor,
              boxShadow: `0 0 0 3px color-mix(in srgb, ${chosenColor} 28%, transparent)`,
            }}
          />
        </div>
      </div>
      <div data-testid={`${testId}-labels`} className="relative h-4 w-full px-2 text-[11px]" aria-hidden="true">
        {THINKING_STOPS.map((s, i) => (
          <span
            key={s.label}
            className={cn(
              'absolute -translate-x-1/2 whitespace-nowrap',
              i === index ? 'font-semibold' : 'text-muted-foreground',
            )}
            style={{
              left: `calc(0.5rem + (100% - 1rem) * ${i / AUTO_STOP})`,
              ...(i === index ? { color: chosenNameColor } : {}),
            }}
          >
            {s.label}
          </span>
        ))}
      </div>
      {disabled && (
        <span id={reasonId} className="sr-only" data-testid={`${testId}-reason`}>
          {disabledReason}
        </span>
      )}
    </div>
  );

  const body = <span className="inline-flex w-full">{slider}</span>;
  return tooltip ? <Tip label={hint}>{body}</Tip> : body;
}
